import React from "react";
import { useAppConfig } from "@/appConfig";
import { getMapStyleRef } from "@/helpers/mapbox";
import { useTourSamples } from "./samples";

const encodeValue = (value) => {
  let v = value < 0 ? ~(value << 1) : value << 1;
  let out = "";
  while (v >= 0x20) {
    out += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  return out + String.fromCharCode(v + 63);
};

export const encodePolyline = (points) => {
  let lastLat = 0;
  let lastLng = 0;
  return points
    .map(([lng, lat]) => {
      const la = Math.round(lat * 1e5);
      const ln = Math.round(lng * 1e5);
      const chunk = encodeValue(la - lastLat) + encodeValue(ln - lastLng);
      lastLat = la;
      lastLng = ln;
      return chunk;
    })
    .join("");
};

const circleAround = (lng, lat, radiusDeg) => {
  const stretch = 1 / Math.cos((lat * Math.PI) / 180);
  const points = Array.from({ length: 33 }, (_, i) => {
    const angle = (i / 32) * 2 * Math.PI;
    return [lng + radiusDeg * stretch * Math.cos(angle), lat + radiusDeg * Math.sin(angle)];
  });
  return [points];
};

const zipsAround = (lng, lat) => {
  const d = 0.045;
  const tile = (x, y, jitter) => [
    [lng + x * d + jitter, lat + y * d],
    [lng + (x + 1) * d, lat + y * d + jitter],
    [lng + (x + 1) * d - jitter, lat + (y + 1) * d],
    [lng + x * d, lat + (y + 1) * d - jitter],
    [lng + x * d + jitter, lat + y * d],
  ];
  return [tile(-1, -1, 0.006), tile(0, -1, -0.005), tile(-1, 0, 0.004)];
};

const blobAround = (lng, lat) => {
  const shape = [
    [-0.06, 0.04], [0.0, 0.065], [0.055, 0.03], [0.07, -0.02], [0.02, -0.06], [-0.045, -0.05], [-0.075, -0.005], [-0.06, 0.04],
  ];
  return [shape.map(([x, y]) => [lng + x, lat + y])];
};

// The Static Images API rejects URLs over ~8 KB, and a hand-drawn tenant area can have hundreds of points.
export const MAX_RING_POINTS = 60;

export const thinRing = (ring, max = MAX_RING_POINTS) => {
  if (ring.length <= max) return ring;
  const step = Math.ceil(ring.length / max);
  const kept = ring.filter((_, i) => i % step === 0);
  const first = ring[0];
  const last = kept[kept.length - 1];
  return last === first || (last[0] === first[0] && last[1] === first[1]) ? kept : [...kept, first];
};

export const staticMapUrl = (rings) => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token) return null;
  const { owner, styleId } = getMapStyleRef();
  const overlays = rings
    .map((ring) => `path-2+2f6fe4-0.95+2f6fe4-0.22(${encodeURIComponent(encodePolyline(thinRing(ring)))})`)
    .join(",");
  return `https://api.mapbox.com/styles/v1/${owner}/${styleId}/static/${overlays}/auto/240x170@2x?padding=18&access_token=${token}&attribution=false&logo=false`;
};

const LocationOptions = () => {
  const { tenant } = useAppConfig() || {};
  const samples = useTourSamples(true);
  const market = tenant?.defaultMarket || {};
  const lat = Number(market.latitude);
  const lng = Number(market.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const area = samples?.wantListing?.area;
  const options = [
    { label: "Radius", rings: circleAround(lng, lat, 0.07) },
    { label: "ZIP codes", rings: zipsAround(lng, lat) },
    { label: "Draw an area", rings: Array.isArray(area) && area.length > 2 ? [area] : blobAround(lng, lat) },
  ];
  return (
    <div className="member-tour-locations" aria-hidden="true">
      {options.map((option) => {
        const url = staticMapUrl(option.rings);
        return (
          <figure key={option.label} className="member-tour-locations__item">
            {url ? <img src={url} alt="" /> : <span className="member-tour-locations__blank" />}
            <figcaption>{option.label}</figcaption>
          </figure>
        );
      })}
    </div>
  );
};

export default LocationOptions;
